import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * 인라인 편집 필드 — 낙관적 + 디바운스 저장(400–600ms) + blur 즉시 (03 §6).
 * 외부 값 변경(undo 등)은 포커스 중이 아닐 때만 반영.
 */
export function EditableText({
  value,
  onCommit,
  multiline = false,
  placeholder,
  className,
  debounceMs = 500,
}: {
  value: string;
  onCommit: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
}) {
  const [local, setLocal] = useState(value);
  const [focused, setFocused] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!focused) setLocal(value);
  }, [value, focused]);

  // unmount/엔티티 전환 시 pending 디바운스 타이머 정리 (stale commit 방지)
  useEffect(() => () => clearTimeout(timer.current), []);

  // textarea 자동 높이
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    }
  }, [local, multiline]);

  const schedule = (v: string) => {
    setLocal(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(v), debounceMs);
  };
  const flush = () => {
    clearTimeout(timer.current);
    setFocused(false);
    if (local !== value) onCommit(local);
  };

  const shared = {
    value: local,
    placeholder,
    onFocus: () => setFocused(true),
    onBlur: flush,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      schedule(e.target.value),
    className: cn('edit-field block w-full resize-none bg-transparent text-sm text-ink', className),
    spellCheck: false,
  };

  if (multiline) {
    return <textarea ref={taRef} rows={1} {...shared} />;
  }
  return (
    <input
      {...shared}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
